import {
    Button,
} from "@mrflark/ui";
import {
    Search,
    X,
} from "lucide-preact";
import type {
    ReactElement,
} from "preact/compat";

import {
    useViewerStore,
} from "../../stores/viewerStore.ts";

type SearchPanelProps = {
    onSearch: () => void;
    onClearSearchAndBrowse: () => void;
};

export const SearchPanel = ({
    onSearch,
    onClearSearchAndBrowse,
}: SearchPanelProps): ReactElement => {
    const {
        searchQuery,
        searchTotalCount,
        searchResultsIndex,
        isSearchActive,
        isSearching,
        queryInterpretation,
        setSearchQuery,
        clearSearch,
    } = useViewerStore();

    return (
        <div className="space-y-4">
            <div className="relative">
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(event) =>
                        setSearchQuery(
                            (event.target as HTMLInputElement).value,
                        )}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" && searchQuery.trim()) {
                            onSearch();
                        }
                    }}
                    className="input w-full pl-9 pr-8"
                    placeholder="funny cat, twitter screenshot..."
                />
                <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
                />
                {searchQuery && (
                    <button
                        type="button"
                        onClick={() => {
                            setSearchQuery("");
                            clearSearch();
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
                    >
                        <X size={16} />
                    </button>
                )}
            </div>

            <p className="text-xs text-text-muted">
                Searches full-text, keywords, synonyms, and AI metadata
            </p>

            <Button
                onClick={onSearch}
                disabled={!searchQuery.trim() || isSearching}
                className="w-full gap-2"
            >
                <Search size={16} />
                {isSearching ? "Searching..." : "Search"}
            </Button>

            {isSearchActive && (
                <button
                    type="button"
                    onClick={onClearSearchAndBrowse}
                    className="w-full text-sm text-text-secondary hover:text-text-primary transition-colors"
                >
                    Clear search & return to browsing
                </button>
            )}

            {queryInterpretation && (
                <div className="p-2 bg-bg-tertiary rounded-lg text-xs space-y-1">
                    <div className="text-text-secondary">Interpreted as:</div>
                    {queryInterpretation.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                            {queryInterpretation.keywords.map((
                                keyword,
                                index,
                            ) => (
                                <span
                                    key={index}
                                    className="px-1.5 py-0.5 bg-accent/20 text-accent rounded"
                                >
                                    {keyword}
                                </span>
                            ))}
                        </div>
                    )}
                    {queryInterpretation.negations.length > 0 && (
                        <div className="text-text-secondary">
                            Excluding: {queryInterpretation.negations.map((
                                negation,
                                index,
                            ) => (
                                <span key={index} className="text-error ml-1">
                                    !{negation}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {isSearchActive && searchTotalCount > 0 && (
                <div className="text-sm text-text-secondary text-center">
                    {searchResultsIndex + 1} of {searchTotalCount} results
                </div>
            )}
        </div>
    );
};
